// DE-PROFILE-LIVE (Andrei, 2026-10-30): where a DE's profile differs from the default we shipped
// (`profile_default`, set by the profile sync). The DE gets the default as its base and the
// company's edits as additions on top, so this shows which fields the company changed: a
// "Differs from default" chip that opens the differences, and, for managers, "Reset to default"
// per field (with a confirm). Nothing when no default was shipped or nothing differs.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  InfoTip,
  toast,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RotateCcwIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { DeProfile } from "../../api/types";
import { type CompanyAccess, isManager } from "../company";
import { keys } from "../queries";
import { problemText } from "./de-status";

const FIELDS = [
  ["role", "Role"],
  ["goal", "Goal"],
  ["backstory", "Backstory"],
  ["standing_orders", "Standing orders"],
] as const;
type Field = (typeof FIELDS)[number][0];

const clean = (s: string | null | undefined) => (s?.trim() ? s.trim() : null);

/** The fields whose text differs from the shipped default (none without a default). */
export function differingFields(
  de: Pick<DeProfile, "profile_default" | Field>,
): (typeof FIELDS)[number][] {
  const d = de.profile_default;
  if (!d) return [];
  return FIELDS.filter(([k]) => clean(de[k]) !== clean(d[k]));
}

const Text = ({ text }: { text: string | null }) =>
  text ? (
    <p className="max-h-40 overflow-y-auto text-sm whitespace-pre-line text-foreground [scrollbar-width:thin]">
      {text}
    </p>
  ) : (
    <p className="text-sm text-muted-foreground">(empty)</p>
  );

export function ProfileDefaultDiff({
  api,
  company,
  de,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeProfile;
}) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState<(typeof FIELDS)[number] | null>(null);
  const qc = useQueryClient();
  const reset = useMutation({
    mutationFn: (field: Field) =>
      api.client.digitalEmployees.update({
        params: { co: company.id, id: de.membership_id },
        body: { [field]: de.profile_default?.[field] ?? null },
      }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: keys.de(company.id, de.membership_id) }),
        qc.invalidateQueries({ queryKey: keys.digitalEmployees(company.id) }),
      ]);
      toast.add({ title: "Reset to default", type: "success" });
    },
    onError: (e) => toast.add({ title: problemText(e, "Not reset."), type: "error" }),
  });
  const diffs = differingFields(de);
  if (diffs.length === 0) return null;
  const manager = isManager(company);
  return (
    <span className="inline-flex items-center gap-1 self-start" data-slot="profile-differs">
      <button
        type="button"
        className="rounded-md focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        onClick={() => setOpen(true)}
        aria-label="View differences from default"
      >
        <Badge variant="info">Differs from default</Badge>
      </button>
      <InfoTip label="About the default">
        The default is the profile we shipped. The DE follows it as its base and your edits on top;
        where an edit conflicts, yours wins.
      </InfoTip>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl" data-slot="profile-diff-dialog">
          <DialogHeader>
            <DialogTitle>Differs from default</DialogTitle>
            <DialogDescription>
              {diffs.length === 1 ? "One field" : `${diffs.length} fields`} changed from the shipped
              default.
            </DialogDescription>
          </DialogHeader>
          <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto [scrollbar-width:thin]">
            {diffs.map((f) => {
              const [k, label] = f;
              return (
                <section
                  key={k}
                  className="flex flex-col gap-2 border-b border-border pb-4 last:border-b-0"
                  data-slot={`diff-${k}`}
                  aria-label={label}
                >
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold">{label}</h3>
                    {manager ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-auto"
                        disabled={reset.isPending}
                        onClick={() => setConfirm(f)}
                        data-slot={`reset-${k}`}
                      >
                        <RotateCcwIcon aria-hidden="true" />
                        Reset to default
                      </Button>
                    ) : null}
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="flex min-w-0 flex-col gap-1">
                      <span className="text-xs font-medium text-muted-foreground uppercase">
                        Default
                      </span>
                      <Text text={clean(de.profile_default?.[k])} />
                    </div>
                    <div className="flex min-w-0 flex-col gap-1">
                      <span className="text-xs font-medium text-muted-foreground uppercase">
                        Now
                      </span>
                      <Text text={clean(de[k])} />
                    </div>
                  </div>
                </section>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
      <AlertDialog open={confirm !== null} onOpenChange={(o) => (o ? null : setConfirm(null))}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset {confirm?.[1].toLowerCase()} to default?</AlertDialogTitle>
            <AlertDialogDescription>
              Your edit is replaced by the shipped default. The DE gets it on its next run.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep my edit</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirm) reset.mutate(confirm[0]);
                setConfirm(null);
              }}
            >
              Reset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </span>
  );
}
